<template>
  <div id="login-page" class="relative min-h-screen overflow-hidden bg-slate-50 dark:bg-slate-950" :dir="isRTL === true ? 'rtl' : 'ltr'">
    <div class="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top,rgba(148,163,184,0.16),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.1),transparent_30%)] dark:bg-[radial-gradient(circle_at_top,rgba(51,65,85,0.5),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(37,99,235,0.16),transparent_28%)]"></div>
    <div class="absolute left-[12%] top-[10%] z-0 h-44 w-44 rounded-full bg-slate-200/50 blur-3xl dark:bg-slate-800/40"></div>
    <div class="absolute bottom-[12%] right-[10%] z-0 h-52 w-52 rounded-full bg-blue-100/40 blur-3xl dark:bg-slate-800/40"></div>
    <div class="absolute inset-0 z-0 flex flex-wrap content-start overflow-hidden pointer-events-none" style="width: 100vw; height: 100vh;">
      <div
        v-for="(pixelTone, index) in pixelStates"
        :key="index"
        class="h-[40px] w-[40px] border-b border-r border-slate-200/50 transition-colors duration-1000 dark:border-slate-800/60"
        :class="getPixelClass(pixelTone)"
      ></div>
    </div>

    <div class="relative z-10 mx-auto flex min-h-screen w-full max-w-[500px] items-center justify-center px-6 py-8">
      <div class="w-full rounded-xl border border-slate-200 bg-white p-8 sm:p-12 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div class="mb-10 text-center">
          <div class="mb-5 flex justify-center">
            <div class="relative h-16 w-44">
              <div v-if="showLoginLogoSkeleton === true" class="absolute inset-0 rounded-xl bg-slate-100 shadow-inner dark:bg-slate-800">
                <div class="h-full w-full animate-pulse rounded-xl bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800"></div>
              </div>
              <img
                v-show="showLoginLogoSkeleton !== true"
                :key="'login-brand-logo-' + String(logoReloadKey)"
                :src="displayLoginLogoSrc"
                alt="Logo"
                class="mx-auto h-16 max-w-[176px] object-contain"
                @load="handleLoginLogoLoad"
                @error="handleLoginLogoError"
              />
            </div>
          </div>
          <div class="mb-6 flex justify-center">
            <div class="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-800/80">
              <span class="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">AI Agents Platform</span>
            </div>
          </div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white">
            {{ loginEyebrow }}
          </h1>
          <p class="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {{ loginDescription }}
          </p>
        </div>

        <div v-if="authAppSetupStep === true" class="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/40">
          <div class="mb-2 flex items-center text-emerald-800 dark:text-emerald-300">
            <svg class="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 class="text-sm font-bold">{{ t('login.authAppSetupTitle', 'Set up your authenticator app') }}</h3>
          </div>
          <p class="mb-4 text-sm text-emerald-700 dark:text-emerald-200">{{ t('login.authAppSetupDescription', 'Scan the QR code with your authenticator app, then return to log in again.') }}</p>
          <div class="flex flex-col items-center md:flex-row">
            <div class="mb-4 w-full shrink-0 md:mb-0 md:mr-4 md:w-32">
              <img v-if="safeAuthAppQrCode !== ''" :src="safeAuthAppQrCode" alt="Authenticator QR code" class="rounded-lg border border-emerald-200 bg-white p-2 shadow-sm dark:border-emerald-900/50 dark:bg-slate-900" />
            </div>
            <div class="w-full flex-1">
              <div class="mb-3 rounded-lg border border-emerald-200 bg-white p-3 shadow-sm dark:border-emerald-900/50 dark:bg-slate-900">
                <div class="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-300">{{ t('login.manualCodeLabel', 'Or enter this code manually') }}</div>
                <div class="mt-1 break-all text-sm font-bold text-slate-800 dark:text-white">{{ authAppSecret }}</div>
              </div>
            </div>
          </div>
          <button
            type="button"
            class="mt-4 flex h-12 w-full items-center justify-center rounded-lg bg-emerald-600 font-bold text-white shadow-md transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-emerald-500 dark:text-slate-950 dark:hover:bg-emerald-400"
            @click="$emit('returnToLogin')"
          >
            {{ t('login.backToLogin', 'Back to login') }}
          </button>
        </div>

        <div v-if="errorMessage !== '' && errorMessage !== null && errorMessage !== undefined" class="mb-5 flex items-start rounded-lg border border-rose-200 bg-rose-50 p-4 shadow-sm dark:border-rose-900/50 dark:bg-rose-950/30">
          <svg class="mt-0.5 mr-3 h-5 w-5 shrink-0 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-sm font-bold text-rose-800 dark:text-rose-200">{{ errorMessage }}</p>
        </div>

        <div v-if="authAppSetupStep !== true">
          <div v-if="mfaStep !== true">
            <div class="mb-4">
              <label class="form-label text-slate-700 dark:text-slate-300">{{ t('login.organizationName', 'Organization') }}</label>
              <input
                type="text"
                :value="organizationName"
                @input="$emit('update:organizationName', $event.target.value)"
                class="form-input h-12 border-slate-200 bg-slate-50 shadow-sm focus:border-blue-500 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-blue-400"
                :placeholder="t('login.organizationNamePlaceholder', 'Enter your organization name')"
                :dir="isRTL === true ? 'rtl' : 'ltr'"
              />
            </div>

            <div class="mb-4">
              <label class="form-label text-slate-700 dark:text-slate-300">{{ t('login.username', 'Username') }}</label>
              <input
                type="text"
                :value="userName"
                @input="$emit('update:userName', $event.target.value)"
                @blur="emitTrimmedUserName"
                class="form-input h-12 border-slate-200 bg-slate-50 shadow-sm focus:border-blue-500 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-blue-400"
                :placeholder="t('login.usernamePlaceholder', 'Email or username')"
              />
            </div>

            <div class="mb-6">
              <label class="form-label text-slate-700 dark:text-slate-300">{{ t('login.password', 'Password') }}</label>
              <input
                type="password"
                :value="password"
                @input="$emit('update:password', $event.target.value)"
                @blur="emitTrimmedPassword"
                @keyup.enter="$emit('login')"
                class="form-input h-12 border-slate-200 bg-slate-50 shadow-sm focus:border-blue-500 focus:bg-white dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-blue-400"
                :placeholder="t('login.passwordPlaceholder', 'Password')"
              />
            </div>
          </div>

          <div v-if="mfaStep === true" class="mb-6 rounded-xl border border-blue-100 bg-blue-50/50 p-6 text-center shadow-inner dark:border-blue-900/40 dark:bg-blue-950/20">
            <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <label class="mb-2 block text-sm font-bold text-slate-800 dark:text-white">{{ resolvedMfaInputLabel }}</label>
            <p class="mb-4 text-sm text-slate-500 dark:text-slate-400">{{ resolvedMfaDescription }}</p>
            <div class="-mx-1 mb-2 flex justify-center" dir="ltr" @paste="handleOtpPaste($event, 0)">
              <div v-for="i in 6" :key="i" class="px-1">
                <input
                  :ref="el => setOtpRef(el, i - 1)"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="1"
                  class="form-input h-14 w-12 border-slate-300 bg-white text-center text-xl font-bold shadow-sm focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-blue-400"
                  :value="otpDigits[i - 1]"
                  @input="handleOtpInput(i - 1, $event)"
                  @keydown="handleOtpKeydown(i - 1, $event)"
                  @paste="handleOtpPaste($event, i - 1)"
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            :disabled="isLoginButtonLoading === true"
            class="relative flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-slate-900 to-slate-800 font-bold text-white shadow-lg transition-all duration-200 hover:from-slate-800 hover:to-slate-700 hover:shadow-xl active:scale-[0.98] disabled:cursor-not-allowed disabled:from-slate-400 disabled:to-slate-400 disabled:opacity-70 dark:from-slate-950 dark:to-slate-900 dark:hover:from-slate-900 dark:hover:to-slate-800"
            @click="mfaStep === true ? $emit('verifyOtp') : $emit('login')"
          >
            <span :class="{ 'opacity-0': isLoginButtonLoading === true }">
              {{ mfaStep === true ? resolvedMfaButtonLabel : t('login.loginButton', 'Sign in') }}
            </span>
            <div v-if="isLoginButtonLoading === true" class="absolute inset-0 flex items-center justify-center">
              <Icon icon="svg-spinners:90-ring-with-bg" class="h-5 w-5" />
            </div>
          </button>

          <button
            v-if="mfaStep === true"
            type="button"
            class="mt-4 w-full text-center text-sm font-bold text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"
            @click="$emit('returnToLogin')"
          >
            {{ t('login.backToLogin', 'Back to login') }}
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@iconify/vue';
import { useI18n } from '@/i18n';
import { globalState } from '@/globalState';
import { computed, onMounted, onUnmounted, ref } from 'vue';

const { t } = useI18n();

const props = defineProps({
  userName: String,
  password: String,
  organizationName: String,
  errorMessage: String,
  isLoading: Boolean,
  isOtpLoading: Boolean,
  mfaStep: Boolean,
  authAppSetupStep: Boolean,
  otpDigits: Array,
  isRTL: Boolean,
  safeAuthAppQrCode: String,
  authAppSecret: String,
  mfaDescription: String,
  mfaInputLabel: String,
  mfaButtonLabel: String
});

const emit = defineEmits([
  'update:userName',
  'update:password',
  'update:organizationName',
  'update:otpDigits',
  'login',
  'verifyOtp',
  'returnToLogin'
]);

function emitTrimmedUserName() {
  const value = typeof props.userName === 'string' ? props.userName.trim() : '';
  emit('update:userName', value);
}

function emitTrimmedPassword() {
  const value = typeof props.password === 'string' ? props.password.trim() : '';
  emit('update:password', value);
}

const otpRefs = ref([]);
const pixelStates = ref([]);
const vendorLogoSrc = ref('');
const logoLoaded = ref(false);
const logoLoadFailed = ref(false);
const logoResolving = ref(true);
const logoReloadKey = ref(0);
let pixelInterval = null;
const pixelToneClasses = [
  'bg-transparent',
  'bg-slate-200/70 dark:bg-slate-800/72',
  'bg-slate-300/55 dark:bg-slate-700/62',
  'bg-blue-100/65 dark:bg-slate-700/52',
  'bg-indigo-100/50 dark:bg-slate-800/60'
];

const loginEyebrow = computed(() => {
  if (props.authAppSetupStep === true) {
    return t('login.authAppSetupTitle', 'Set up your authenticator app');
  }
  if (props.mfaStep === true) {
    return t('login.twoFactorAuthentication', 'Two-Factor Authentication');
  }
  return t('vendorSignIn', 'Sign in');
});

const loginDescription = computed(() => {
  if (props.authAppSetupStep === true) {
    return t('login.authAppSetupDescription', 'Scan the QR code with your authenticator app, then return to log in again.');
  }
  if (props.mfaStep === true) {
    return props.mfaDescription;
  }
  return t('login.enterCredentials', 'Please enter your credentials to continue.');
});

const envLoginLogoSrc = computed(() => {
  const whitelabel = globalState.whitelabel !== undefined && globalState.whitelabel !== null ? globalState.whitelabel : null;
  const rawLogo = whitelabel !== null && typeof whitelabel.logoUrl === 'string' ? whitelabel.logoUrl : '';
  return sanitizeLoginLogoUrl(rawLogo);
});

const fallbackLoginLogoSrc = computed(() => {
  if (envLoginLogoSrc.value !== '') {
    return envLoginLogoSrc.value;
  }
  return '/logo.svg';
});

const shouldUseVendorLoginLogo = computed(() => vendorLogoSrc.value !== '' && logoLoadFailed.value !== true);

const displayLoginLogoSrc = computed(() => {
  if (shouldUseVendorLoginLogo.value === true) {
    return vendorLogoSrc.value;
  }
  return fallbackLoginLogoSrc.value;
});

const showLoginLogoSkeleton = computed(() => {
  if (logoResolving.value === true) {
    return true;
  }
  if (logoLoaded.value !== true) {
    return true;
  }
  return false;
});

const isLoginButtonLoading = computed(() => {
  if (props.isLoading === true) {
    return true;
  }
  if (props.isOtpLoading === true) {
    return true;
  }
  return false;
});

const resolvedMfaDescription = computed(() => {
  if (typeof props.mfaDescription === 'string' && props.mfaDescription.trim().length > 0) {
    return props.mfaDescription;
  }
  return t('login.enterVerificationCode', 'Enter your verification code');
});

const resolvedMfaInputLabel = computed(() => {
  if (typeof props.mfaInputLabel === 'string' && props.mfaInputLabel.trim().length > 0) {
    return props.mfaInputLabel;
  }
  return t('login.verificationCode', 'Verification Code');
});

const resolvedMfaButtonLabel = computed(() => {
  if (typeof props.mfaButtonLabel === 'string' && props.mfaButtonLabel.trim().length > 0) {
    return props.mfaButtonLabel;
  }
  return t('login.verifyOtp', 'Verify');
});

function isProductionEnv() {
  return process.env.environment === 'production';
}

function getCurrentOrigin() {
  if (typeof window === 'undefined') {
    return '';
  }
  if (window.location === undefined) {
    return '';
  }
  if (window.location === null) {
    return '';
  }
  return typeof window.location.origin === 'string' ? window.location.origin : '';
}

function sanitizeLoginLogoUrl(rawValue) {
  if (typeof rawValue !== 'string') {
    return '';
  }
  const value = rawValue.trim();
  if (value === '') {
    return '';
  }
  if (value.startsWith('data:image/') === true) {
    return value;
  }
  if (value.startsWith('/') === true) {
    return value;
  }
  if (typeof window === 'undefined') {
    return '';
  }
  if (window.location === undefined) {
    return '';
  }
  if (window.location === null) {
    return '';
  }
  let parsed = null;
  try {
    parsed = new URL(value, window.location.origin);
  } catch (error) {
    return '';
  }
  const protocol = typeof parsed.protocol === 'string' ? parsed.protocol.toLowerCase() : '';
  if (protocol === 'https:') {
    return parsed.href;
  }
  if (protocol === 'http:' && isProductionEnv() === false) {
    return parsed.href;
  }
  return '';
}

function getVendorBrandingUrl() {
  const apiBase = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint : '';
  const origin = getCurrentOrigin();
  const query = origin !== '' ? '?origin=' + encodeURIComponent(origin) : '';
  return `${apiBase}/api/organizationSettings/public/vendorBranding${query}`;
}

async function fetchVendorLoginLogo() {
  const url = getVendorBrandingUrl();
  try {
    const response = await fetch(url, {
      credentials: 'include',
      cache: 'no-store'
    });
    if (response.ok !== true) {
      return { matched: false, logo: '' };
    }
    const data = await response.json();
    const matched = data !== null && data !== undefined && data.matched === true;
    const vendorBranding = matched === true && data.vendorBranding !== undefined && data.vendorBranding !== null ? data.vendorBranding : null;
    const vendorLogo = vendorBranding !== null && typeof vendorBranding.vendorLogo === 'string' ? vendorBranding.vendorLogo : '';
    return { matched: matched, logo: sanitizeLoginLogoUrl(vendorLogo) };
  } catch (error) {
    return { matched: false, logo: '' };
  }
}

async function loadVendorLoginLogo() {
  logoResolving.value = true;
  logoLoadFailed.value = false;
  const previousDisplayLogoSrc = displayLoginLogoSrc.value;
  const resolvedVendorLogo = await fetchVendorLoginLogo();
  vendorLogoSrc.value = typeof resolvedVendorLogo.logo === 'string' ? resolvedVendorLogo.logo : '';
  if (displayLoginLogoSrc.value !== previousDisplayLogoSrc) {
    logoLoaded.value = false;
  }
  logoReloadKey.value = logoReloadKey.value + 1;
  logoResolving.value = false;
}

function handleLoginLogoLoad() {
  logoLoaded.value = true;
}

function handleLoginLogoError() {
  if (shouldUseVendorLoginLogo.value === true && displayLoginLogoSrc.value === vendorLogoSrc.value) {
    logoLoadFailed.value = true;
    logoLoaded.value = false;
    logoReloadKey.value = logoReloadKey.value + 1;
    return;
  }
  logoLoaded.value = true;
}

function updatePixels() {
  const cols = Math.ceil(window.innerWidth / 40);
  const rows = Math.ceil(window.innerHeight / 40);
  const total = cols * rows;
  if (pixelStates.value.length !== total) {
    pixelStates.value = Array(total).fill(0);
  }
}

function getPixelClass(pixelTone) {
  return pixelToneClasses[pixelTone] !== undefined ? pixelToneClasses[pixelTone] : pixelToneClasses[0];
}

onMounted(() => {
  loadVendorLoginLogo();
  updatePixels();
  window.addEventListener('resize', updatePixels);
  pixelInterval = setInterval(() => {
    const availableIndices = [];
    for (let i = 0; i < pixelStates.value.length; i = i + 1) {
      if (pixelStates.value[i] === 0) {
        availableIndices.push(i);
      }
    }
    if (availableIndices.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableIndices.length);
      const nextShade = Math.floor(Math.random() * (pixelToneClasses.length - 1)) + 1;
      pixelStates.value[availableIndices[randomIndex]] = nextShade;
    } else {
      const resetCount = Math.max(1, Math.floor(pixelStates.value.length * 0.08));
      let count = 0;
      while (count < resetCount) {
        const index = Math.floor(Math.random() * pixelStates.value.length);
        pixelStates.value[index] = 0;
        count = count + 1;
      }
    }
  }, 65);
});

onUnmounted(() => {
  if (pixelInterval !== null) {
    clearInterval(pixelInterval);
  }
  window.removeEventListener('resize', updatePixels);
});

function setOtpRef(el, index) {
  if (typeof index === 'number' && el !== null && el !== undefined) {
    otpRefs.value[index] = el;
  }
}

function focusOtpIndex(index) {
  if (index >= 0 && index < 6 && otpRefs.value[index] !== null && otpRefs.value[index] !== undefined) {
    otpRefs.value[index].focus();
    if (typeof otpRefs.value[index].select === 'function') {
      otpRefs.value[index].select();
    }
  }
}

function updateOtpDigits(newDigits) {
  emit('update:otpDigits', newDigits);
}

function handleOtpInput(index, event) {
  const value = event.target.value;
  const digit = value.replace(/\D/g, '').slice(0, 1);
  const newDigits = [...props.otpDigits];
  newDigits[index] = digit;
  updateOtpDigits(newDigits);
  if (digit !== '' && index < 5) {
    focusOtpIndex(index + 1);
  }
}

function handleOtpKeydown(index, event) {
  const key = event.key;
  if (key === 'Backspace') {
    if (props.otpDigits[index] === '') {
      if (index > 0) {
        focusOtpIndex(index - 1);
      }
    } else {
      const newDigits = [...props.otpDigits];
      newDigits[index] = '';
      updateOtpDigits(newDigits);
    }
  } else if (key === 'ArrowLeft' && index > 0) {
    focusOtpIndex(index - 1);
  } else if (key === 'ArrowRight' && index < 5) {
    focusOtpIndex(index + 1);
  }
}

function handleOtpPaste(event, startIndex) {
  event.preventDefault();
  const clipboardData = event !== null && event !== undefined ? event.clipboardData : null;
  const text = clipboardData !== null && clipboardData !== undefined && typeof clipboardData.getData === 'function' ? clipboardData.getData('text') : '';
  const digits = text.replace(/\D/g, '');
  const newDigits = [...props.otpDigits];
  let i = 0;
  while (i < digits.length && startIndex + i < 6) {
    newDigits[startIndex + i] = digits.charAt(i);
    i = i + 1;
  }
  updateOtpDigits(newDigits);
  const nextIndex = Math.min(startIndex + i, 5);
  focusOtpIndex(nextIndex);
}
</script>

<style scoped>
:deep(.Vue-Toastification__container) {
  display: none !important;
}

.form-input {
  width: 100%;
  border-width: 1px;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  outline: none;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.02);
}

.form-input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15), inset 0 1px 2px rgba(0, 0, 0, 0.02);
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
}

</style>
